import { unionBy } from 'lodash';
import { DeepResearch } from '../../libs/types/src/entities/DeepResearch';
import {
  AGENT_STATUS_RESET,
  AGENT_STATUS_UPDATE,
  CHAT_FAILURE,
  CHAT_REQUEST,
  CHAT_SUCCESS,
  CLEAR_CHAT_ERROR,
  CLEAR_CHAT_HISTORY,
  CLEAR_RESEARCH_ERROR,
  CLEAR_RESEARCH_HISTORY,
  EXPORT_DOCUMENT_FAILURE,
  EXPORT_DOCUMENT_REQUEST,
  EXPORT_DOCUMENT_SUCCESS,
  GET_CHAT_MESSAGES_FAILURE,
  GET_CHAT_MESSAGES_REQUEST,
  GET_CHAT_MESSAGES_SUCCESS,
  GET_RESEARCH_FAILURE,
  GET_RESEARCH_REQUEST,
  GET_RESEARCH_SUCCESS,
  INJECT_USER_MESSAGE,
  SET_ONGOING_RESEARCH_STATUS,
  SET_RESEARCH_STATUS,
  START_RESEARCH_FAILURE,
  START_RESEARCH_REQUEST,
  START_RESEARCH_SUCCESS,
} from './deepResearchActions';

export interface DeepResearchState {
  currentResearch: DeepResearch | null;
  researchHistory: DeepResearch[];
  researchHistoryMetaData: {
    count: number;
    page_size: number;
    page_count: number;
    has_next: boolean;
    next: number;
  };
  startResearchLoading: boolean;
  startResearchError: string;
  getResearchLoading: boolean;
  getResearchError: string;
  chatLoading: boolean;
  chatError: string;
  chatHistory: any[];
  chatHistoryMetaData: {
    count: number;
    page_size: number;
    page_count: number;
    has_next: boolean;
    next: number;
  };
  getChatMessagesLoading: boolean;
  getChatMessagesError: string;
  agentStatus: string[];
  isOngoingResearch: boolean;
  exportDocumentLoading: boolean;
}

const initialState: DeepResearchState = {
  currentResearch: null,
  researchHistory: [],
  researchHistoryMetaData: {
    count: 0,
    page_size: 0,
    page_count: 0,
    has_next: false,
    next: 0,
  },
  startResearchLoading: false,
  startResearchError: '',
  getResearchLoading: false,
  getResearchError: '',
  chatLoading: false,
  chatError: '',
  chatHistory: [],
  chatHistoryMetaData: {
    count: 0,
    page_size: 0,
    page_count: 0,
    has_next: false,
    next: 0,
  },
  agentStatus: [],
  getChatMessagesLoading: false,
  getChatMessagesError: '',
  isOngoingResearch: false,
  exportDocumentLoading: false,
};

export const deepResearchReducers = (state = initialState, action: any): DeepResearchState => {
  switch (action.type) {
    case START_RESEARCH_REQUEST:
      return {
        ...state,
        startResearchLoading: true,
        startResearchError: '',
      };

    case START_RESEARCH_SUCCESS:
      return {
        ...state,
        currentResearch: action.research,
        researchHistory: [action.research, ...state.researchHistory],
        startResearchLoading: false,
        startResearchError: '',
      };

    case START_RESEARCH_FAILURE:
      return {
        ...state,
        startResearchLoading: false,
        startResearchError: action.error,
      };

    case GET_RESEARCH_REQUEST:
      return {
        ...state,
        getResearchLoading: true,
        getResearchError: '',
      };

    case GET_RESEARCH_SUCCESS:
      return {
        ...state,
        researchHistory: unionBy([...state.researchHistory, ...action.research.data], 'id'),
        researchHistoryMetaData: action.research.metadata,
        getResearchLoading: false,
        getResearchError: '',
      };

    case GET_RESEARCH_FAILURE:
      return {
        ...state,
        getResearchLoading: false,
        getResearchError: action.error,
      };

    case CLEAR_RESEARCH_ERROR:
      return {
        ...state,
        startResearchError: '',
        getResearchError: '',
      };

    case SET_RESEARCH_STATUS:
      return {
        ...state,
        researchHistory: state.researchHistory.map((research: DeepResearch) => {
          const item = action.response.data.find((item: any) => item.id === research.id);
          if (item) return ({ ...research, status: item.status, deep_research_analysis_file_url: item.deep_research_analysis_file_url });
          return research;
        }),
      };

    case CLEAR_RESEARCH_HISTORY:
      return {
        ...state,
        researchHistory: [],
        researchHistoryMetaData: {
          count: 0,
          page_size: 0,
          page_count: 0,
          has_next: false,
          next: 0,
        },
      };

    case CLEAR_CHAT_ERROR:
      return {
        ...state,
        chatError: '',
      };

    case CLEAR_CHAT_HISTORY:
      return {
        ...state,
        chatHistory: [],
      };

    case CHAT_REQUEST:
      return {
        ...state,
        chatLoading: true,
        chatError: '',
      };

    case CHAT_SUCCESS:
      return {
        ...state,
        chatLoading: false,
        chatError: '',
        chatHistory: [{ type: 'ai', response: action.response }, ...state.chatHistory],
      };

    case CHAT_FAILURE:
      return {
        ...state,
        chatLoading: false,
        chatError: action.error,
      };

    case INJECT_USER_MESSAGE:
      return {
        ...state,
        chatHistory: [{ type: 'user', query: action.message }, ...state.chatHistory],
      };

    case GET_CHAT_MESSAGES_REQUEST:
      return {
        ...state,
        getChatMessagesLoading: true,
        getChatMessagesError: '',
      };

    case GET_CHAT_MESSAGES_SUCCESS:
      return {
        ...state,
        chatHistory: [...state.chatHistory, ...action.messages.data],
        chatHistoryMetaData: action.messages.metadata,
        getChatMessagesLoading: false,
      };

    case GET_CHAT_MESSAGES_FAILURE:
      return {
        ...state,
        getChatMessagesLoading: false,
        getChatMessagesError: action.error,
      };

    case AGENT_STATUS_UPDATE:
      return {
        ...state,
        agentStatus: [...state.agentStatus, action.status],
      };

    case AGENT_STATUS_RESET:
      return {
        ...state,
        agentStatus: [],
      };

    case SET_ONGOING_RESEARCH_STATUS:
      return {
        ...state,
        isOngoingResearch: action.isProcessing,
      };

    case EXPORT_DOCUMENT_REQUEST:
      return {
        ...state,
        exportDocumentLoading: true,
      };

    case EXPORT_DOCUMENT_SUCCESS:
      return {
        ...state,
        exportDocumentLoading: false,
      };

    case EXPORT_DOCUMENT_FAILURE:
      return {
        ...state,
        exportDocumentLoading: false,
      };

    default:
      return state;
  }
};
